/**
 * CheckoutContext - E-commerce Cart and Stripe Checkout Flow Management
 *
 * Manages the complete checkout process including cart state, customer details,
 * payment intents, and order confirmation. Uses reducer pattern for complex
 * multi-stage checkout flow.
 *
 * Key architectural decisions:
 * - Reducer pattern: Complex checkout flow with multiple stages and state transitions
 * - localStorage persistence: Cart survives page reloads for better UX
 * - Single-item cart: Business rule - only one purchase at a time (prevents cart abandonment)
 * - Stripe integration: Payment intents created on cart add, not at checkout
 *
 * Business rules:
 * - One item per cart: Previous item replaced when new item added
 * - Payment intent creation: Happens immediately on add to cart (not at checkout)
 * - Tax calculation: Server-side via Stripe Tax API
 * - Order persistence: Last order saved for confirmation page
 *
 * Checkout stages:
 * 1. cart - Browsing/adding items
 * 2. checkout/customer - Collecting billing details
 * 3. checkout/payment - Payment method collection
 * 4. order/confirm - Processing payment
 * 5. complete - Order confirmed
 *
 * Performance considerations:
 * - Lazy Stripe loading: Stripe.js loaded only when payment intent created
 * - localStorage caching: Reduces API calls on page reload
 * - Payment intent reuse: Avoids creating duplicate intents
 *
 * @see src/services/apiStripe.js - Stripe API integration
 * @see src/features/payments/CheckoutForm.jsx - Main checkout UI
 * @see .claude/state-management-rules.md - State management patterns
 */
import { createContext, useContext, useState, useReducer, useEffect, useCallback, ReactNode, Dispatch } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { createPaymentIntent, getPublishableKey } from '../../services/apiStripe';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { extractErrorMessage } from './utils/errorHandling';
import { logPaymentError } from './utils/errorHandling';

const CHECKOUT_CLIENT_SECRET_KEY = 'checkout_clientSecret';

/**
 * Cart line item interface
 *
 * WHY flexible structure:
 * - Different product types (subscriptions, matchpasses) have different fields
 * - Metadata varies by product category
 */
export interface LineItem {
  /** Product type (subscription, matchpass, etc.) */
  type: string;

  /** Product identifier */
  product_id: string;

  /** Related entity ID (fixture ID, plan ID) */
  linked_id: string;

  /** Stripe price ID */
  price_id: string;

  /** Display title */
  title: string;

  /** Price in cents */
  price: number;

  /** Home team logo (matchpass only) */
  homeTeamLogo?: string;

  /** Opponent logo (matchpass only) */
  opponentTeamLogo?: string;

  /** Access validity hours (matchpass only) */
  validity?: number;

  /** Audio-only flag */
  is_audio?: boolean;

  /** Stream date (matchpass only) */
  streamDate?: string;

  /** Content delivery format — video (default) or audio-only */
  content_type?: 'video' | 'audio';

  /** Whether content is live or on-demand replay */
  delivery_type?: 'live' | 'on_demand';

  /** Scheduled date for live content (ISO 8601). Only populated when delivery_type is 'live'. */
  scheduled_date?: string;

  /** Billing cycle (subscription only) */
  billing_cycle?: string;

  /** Recurring flag (subscription only) */
  is_recurring?: boolean;

  /**
   * DEV-2132: Pre-formatted renewal-date label for recurring plans with a
   * fixed anchor date (e.g. "Renews 1st June 2026"). Backend supplies the
   * formatted string on the plan; we thread it through verbatim so the cart
   * summary stays consistent with the Plans page (single source of truth).
   * Only populated for recurring `plan` line items, not matchpasses.
   */
  renewal_label?: string;

  /** Thumbnail URL for checkout confirmation display (DEV-1917) */
  thumbnail?: string;

  /** ISO 4217 currency code from backend (DEV-2000: eliminates GBP default flash) */
  currency?: string;

  /** Additional product metadata */
  [key: string]: unknown;
}

/**
 * Cart interface
 */
export interface Cart {
  /** Cart line items */
  lineItems: LineItem[];

  /** Stripe payment intent client secret */
  clientSecret?: string;

  /** Payment intent details */
  payment?: {
    id: string;
    amount?: number;
    currency?: string;
  };
}

/**
 * Customer billing details interface
 */
export interface CustomerDetails {
  /** First name */
  given_name: string;

  /** Last name */
  family_name: string;

  /** Email address */
  email: string;

  /** Phone number */
  phone: string;

  /** Billing address */
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
  };

  /** Customer metadata */
  metadata: {
    /** Internal customer ID (Cognito sub) */
    internal_id: string;
  };
}

/**
 * Payment details interface
 */
export interface PaymentDetails {
  /** Amount in cents */
  amount: number;

  /** Currency code (ISO 4217) */
  currency: string;

  /** Payment method type */
  payment_method: string;

  /** Confirm immediately flag */
  confirm: boolean;

  /** Stripe customer ID */
  customer: string | null;

  /** Payment description */
  description: string;

  /** Payment metadata */
  metadata: Record<string, unknown>;

  /** Receipt email */
  receipt_email: string;

  /** Statement descriptor */
  statement_descriptor: string;

  /** Statement descriptor suffix */
  statement_descriptor_suffix: string;

  /** Return URL after payment */
  return_url: string;

  /** Payment status */
  status: 'unconfirmed' | 'processing' | 'succeeded' | 'failed';
}

/**
 * Enriched cart with tax calculation
 *
 * WHY separate interface:
 * - Returned from server after payment intent creation
 * - Includes calculated tax and total amounts
 */
export interface EnrichedCart extends Cart {
  /** Tax calculation results */
  taxCalculation?: {
    amount_total: number;
    tax_amount: number;
    [key: string]: unknown;
  };

  /** Payment intent details */
  payment?: Partial<PaymentDetails>;

  /** Additional server-added fields */
  [key: string]: unknown;
}

/**
 * Checkout stages
 */
export type CheckoutStage =
  | 'cart'
  | 'checkout/customer'
  | 'checkout/payment'
  | 'order/confirm'
  | 'complete';

/**
 * Checkout state interface
 */
export interface CheckoutState {
  /** Loading state */
  isLoading: boolean;

  /** Current checkout stage */
  stage: CheckoutStage;

  /** Current cart */
  cart: Cart;

  /** Current order (cart moved to checkout) */
  order: EnrichedCart;

  /** Last completed order */
  lastOrder: EnrichedCart;

  /** Customer billing details */
  customer: CustomerDetails;

  /** Payment details */
  payment: PaymentDetails;
}

/**
 * Checkout reducer action types
 */
export type CheckoutAction =
  | { type: 'cart/add'; payload: LineItem }
  | { type: 'cart/remove'; payload: { id: string } }
  | { type: 'cart/set'; payload: Cart }
  | { type: 'cart/checkout' }
  | { type: 'cart/empty' }
  | { type: 'checkout/customer'; payload: CustomerDetails }
  | { type: 'checkout/payment'; payload: PaymentDetails }
  | { type: 'payment/update'; payload: Partial<PaymentDetails> }
  | { type: 'order/force'; payload: EnrichedCart }
  | { type: 'order/confirm' }
  | { type: 'order/confirmed'; payload?: unknown }
  | { type: 'customer/set'; payload: CustomerDetails }
  | { type: 'data/stage'; payload: CheckoutStage }
  | { type: 'data/reset' };

/**
 * Initial cart structure
 */
const initialCart: Cart = {
  lineItems: [],
};

/**
 * Initial checkout state
 */
const initialState: CheckoutState = {
  isLoading: false,
  stage: 'cart',
  cart: { ...initialCart },
  order: { ...initialCart },
  lastOrder: { ...initialCart },
  customer: {
    given_name: '',
    family_name: '',
    email: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
    },
    metadata: {
      internal_id: '',
    },
  },
  payment: {
    amount: 0,
    currency: 'GBP',
    payment_method: 'card',
    confirm: true,
    customer: null,
    description: '',
    metadata: {},
    receipt_email: '',
    statement_descriptor: '',
    statement_descriptor_suffix: '',
    return_url: '',
    status: 'unconfirmed',
  },
};

/**
 * Checkout reducer
 *
 * WHY reducer over useState:
 * - Complex checkout flow with multiple stages
 * - State transitions need to be atomic and predictable
 * - Easier to test checkout flow logic
 * - Better debugging with action history
 *
 * @param state - Current checkout state
 * @param action - Reducer action
 * @returns Updated checkout state
 */
export function reducer(state: CheckoutState, action: CheckoutAction): CheckoutState {
  switch (action.type) {
    case 'cart/add':
      return {
        ...state,
        cart: {
          ...state.cart,
          lineItems: [
            ...state.cart.lineItems.filter(
              (i) => i.linked_id !== action.payload.linked_id && i.type !== action.payload.type
            ),
            action.payload,
          ],
        },
      };

    case 'cart/remove':
      return {
        ...state,
        cart: {
          ...state.cart,
          lineItems: state.cart.lineItems.filter((item) => item.product_id !== action.payload.id),
        },
      };

    case 'cart/set':
      return { ...state, cart: action.payload };

    case 'cart/checkout':
      return {
        ...state,
        stage: 'checkout/customer',
        order: state.cart as EnrichedCart,
      };

    case 'cart/empty':
      return {
        ...state,
        stage: 'cart',
        cart: { ...state.cart, lineItems: [] },
      };

    case 'checkout/customer':
      return {
        ...state,
        customer: action.payload,
        stage: 'checkout/payment',
        payment: {
          ...state.payment,
          amount: (state.order as EnrichedCart).taxCalculation?.amount_total ?? 0,
          description:
            state.order.lineItems.length === 1
              ? state.order.lineItems[0]?.type?.toUpperCase() + ' - ' + state.order.lineItems[0]?.title
              : 'MatchDay Live',
        },
      };

    case 'checkout/payment':
      return { ...state, payment: action.payload };

    case 'payment/update':
      return { ...state, payment: { ...state.payment, ...action.payload } };

    case 'order/force':
      return {
        ...state,
        stage: 'checkout/customer',
        cart: action.payload,
        order: action.payload,
        payment: { ...state.payment, ...(action.payload.payment ?? {}) },
      };

    case 'order/confirm':
      return {
        ...state,
        stage: 'order/confirm',
      };

    case 'order/confirmed':
      return {
        ...state,
        stage: 'complete',
        cart: { ...initialCart },
        lastOrder: { ...state.order },
      };

    case 'customer/set':
      return { ...state, customer: action.payload };

    case 'data/stage':
      return { ...state, stage: action.payload };

    case 'data/reset':
      return { ...initialState };

    default:
      // TypeScript exhaustiveness check
      const _exhaustiveCheck: never = action;
      throw new Error(`Unknown action type: ${JSON.stringify(_exhaustiveCheck)}`);
  }
}

/**
 * Checkout context value interface
 */
export interface CheckoutContextValue {
  /** Current checkout stage */
  stage: CheckoutStage;

  /** Current cart */
  cart: Cart;

  /** Payment details */
  payment: PaymentDetails;

  /** Current order */
  order: EnrichedCart;

  /** Last completed order */
  lastOrder: EnrichedCart;

  /** Loading state */
  isLoading: boolean;

  /** Error message */
  error: string;

  /** Stripe instance promise */
  stripePromise: Promise<Stripe | null> | null;

  /** Stripe client secret */
  stripeClientSecret: string;

  /**
   * Add item to cart (replaces existing item)
   * @param item - Line item to add
   */
  addToCart: (item: LineItem) => Promise<void>;

  /**
   * DEV-1997: Add item to cart synchronously (no payment intent).
   * Stores items in localStorage and context state, clears old payment intent,
   * but does NOT await payment intent creation. Call createPaymentIntentFromCart
   * separately (e.g. on the checkout page) to create the intent.
   */
  addToCartImmediate: (item: LineItem) => void;

  /**
   * DEV-1997: Create payment intent for current cart items.
   * Exposed so CheckoutFormNew can trigger intent creation after navigation
   * instead of blocking on the pre-checkout modal.
   */
  createPaymentIntentFromCart: (items: LineItem[]) => Promise<void>;

  /** Empty the cart */
  emptyCart: () => void;

  /**
   * Remove item from cart
   * @param item - Item to remove
   */
  removeFromCart: (item: LineItem) => Promise<void>;

  /** Move cart to checkout */
  checkoutCart: () => Promise<void>;

  /**
   * Force order state with payment intent
   * @param paymentIntent - Enriched cart from server
   */
  forceOrder: (paymentIntent: EnrichedCart) => Promise<void>;

  /**
   * Add payment details
   * @param data - Payment details
   */
  addPaymentDetails: (data: PaymentDetails) => void;

  /**
   * Confirm order completion
   * @param data - Order confirmation data
   */
  confirmOrder: (data?: unknown) => void;

  /**
   * Update payment details
   * @param data - Partial payment update
   */
  updatePayment: (data: Partial<PaymentDetails>) => void;

  /** Clear checkout state */
  clear: () => void;

  /**
   * Set checkout stage
   * @param stage - New stage
   */
  setStage: (stage: CheckoutStage) => void;

  /**
   * Set Stripe promise
   * @param promise - Stripe instance promise
   */
  setStripePromise: (promise: Promise<Stripe | null> | null) => void;

  /**
   * Set Stripe client secret
   * @param secret - Client secret from payment intent
   */
  setStripeClientSecret: (secret: string) => void;

  /** Get last completed order */
  getLastOrder: () => EnrichedCart;

  /**
   * DEV-2132: Clear the context-level error string.
   *
   * Without this, a failed `handlePaymentIntent` call leaves `error` set
   * forever — once CheckoutFormNew shows the error UI there's no way for the
   * user's "Try again" action to dismiss it. Pairs with the retry-cap reset
   * in CheckoutFormNew to give the user a real recovery path.
   */
  clearError: () => void;
}

/**
 * Create context with undefined default
 */
const CheckoutContext = createContext<CheckoutContextValue | undefined>(undefined);

/**
 * CheckoutContextProvider props interface
 */
export interface CheckoutContextProviderProps {
  children: ReactNode;
}

/**
 * CheckoutContextProvider - E-commerce cart and checkout flow management
 *
 * @param props - Provider props
 */
export function CheckoutContextProvider({ children }: CheckoutContextProviderProps): JSX.Element {
  const LS_CART_KEY = 'cart';
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeClientSecret, setStripeClientSecret] = useState<string>('');
  const [{ stage, cart, payment, order, lastOrder }, dispatch] = useReducer(reducer, initialState);

  const queryClient = useQueryClient();

  /**
   * Create Stripe payment intent
   *
   * WHY create on cart add instead of checkout:
   * - Faster checkout flow (intent already created)
   * - Tax calculation happens upfront
   * - Better error handling before user enters details
   *
   * FIX: Wrapped in useCallback to prevent infinite render loops
   * - Function was recreated on every render, triggering useEffect dependencies
   * - Components using this in useEffect deps would enter infinite loops
   * - Now memoized with stable dependencies ensuring referential equality
   *
   * @param items - Line items to create intent for
   * @param forceCreate - Force creation even if secret exists (fixes React state timing)
   * @returns Enriched cart with payment intent
   */
  const handlePaymentIntent = useCallback(async (items: LineItem[], forceCreate: boolean = false): Promise<EnrichedCart | null> => {
    // CRITICAL FIX: forceCreate bypasses the secret check to handle React async state updates
    // When enrichCart clears the secret and immediately calls this, state hasn't updated yet
    if (stripeClientSecret && !forceCreate) {
      return null;
    }

    setIsLoading(true);

    try {
      // PERFORMANCE FIX: Execute payment intent creation and publishable key fetch in parallel
      // This saves 3-5 seconds by not waiting for payment intent before fetching key
      const [paymentIntent, publishableKeyResponse] = await Promise.all([
        // Payment intent with timeout wrapper
        Promise.race([
          createPaymentIntent({ lineItems: items }),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Payment intent creation timed out after 30 seconds')), 30000)
          )
        ]),
        // Publishable key (no timeout needed, fast operation)
        getPublishableKey()
      ]);

      if (!paymentIntent) {
        return null;
      }

      if ('message' in paymentIntent && paymentIntent.message) {
        const errorMsg = paymentIntent.message || paymentIntent.error || 'Payment intent creation failed';
        setError(typeof errorMsg === 'string' ? errorMsg : 'Payment intent creation failed');
        return null;
      }

      // CRITICAL FIX: Validate Stripe publishable key exists before using
      // WHY: TypeScript migration switched from hardcoded STRIPE_PUBLIC_KEY constant to dynamic API fetch
      // TypeScript types don't enforce runtime validation - API could return null/undefined/malformed response
      // Without this check, loadStripe(undefined) throws "Missing value for Stripe(): apiKey should be a string"

      // BACKEND COMPATIBILITY FIX: Handle both 'publishableKey' (camelCase) and 'publishablekey' (lowercase)
      // WHY: During TypeScript migration, backend started returning lowercase 'publishablekey'
      // but Zod schema expects camelCase 'publishableKey'. This ensures backward compatibility.
      const publishableKey =
        publishableKeyResponse?.publishableKey ||
        (publishableKeyResponse as any)?.publishablekey;

      if (!publishableKey) {
        logPaymentError(
          new Error('Missing Stripe publishable key from API'),
          {
            component: 'CheckoutContext',
            action: 'handlePaymentIntent',
            // Log response structure to debug what backend actually returned
            publishableKeyResponse,
            // Show which field names we checked
            checkedFields: ['publishableKey (camelCase)', 'publishablekey (lowercase)']
          }
        );
        setError('Unable to load payment configuration. Please refresh and try again.');
        return null;
      }

      setStripeClientSecret(paymentIntent.clientSecret);
      // DEV-1936: Persist clientSecret to sessionStorage for page refresh recovery
      // WHY: PaymentIntent remains valid on Stripe's side after refresh, but the
      // local state is lost. sessionStorage is same-tab, same-origin, auto-clears.
      try {
        sessionStorage.setItem(CHECKOUT_CLIENT_SECRET_KEY, paymentIntent.clientSecret);
      } catch (e) { /* ignore quota errors */ }
      setStripePromise(loadStripe(publishableKey));
      dispatch({ type: 'order/force', payload: paymentIntent as EnrichedCart });

      setIsLoading(false);
      return paymentIntent as EnrichedCart;
    } catch (err) {
      // WHY: Use logPaymentError instead of console.error for PCI compliance
      // Prevents logging sensitive Stripe data (client secrets, payment intent IDs)
      logPaymentError(err instanceof Error ? err : new Error(String(err)), {
        component: 'CheckoutContext',
        action: 'handlePaymentIntent',
        itemCount: items.length,
      });
      setError(extractErrorMessage(err));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [stripeClientSecret, setStripeClientSecret, setStripePromise, dispatch]); // Memoize with stable dependencies

  /**
   * Enrich cart with payment intent and tax calculation
   *
   * FIX: Wrapped in useCallback to prevent infinite render loops
   * - Now memoized with stable dependencies ensuring referential equality
   *
   * @param item - Optional item to add to cart
   */
  const enrichCart = useCallback(async (item?: LineItem): Promise<void> => {
    const newCartItems = item ? [item] : [...cart.lineItems];

    // CRITICAL FIX: Clear old payment intent secret to ensure fresh intent creation
    // Without this, handlePaymentIntent returns null if secret already exists from failed attempt
    setStripeClientSecret('');
    try { sessionStorage.removeItem(CHECKOUT_CLIENT_SECRET_KEY); } catch (e) { /* ignore */ }

    setCartInLocalStorage({
      lineItems: [...newCartItems],
    });

    dispatch({
      type: 'cart/set',
      payload: {
        lineItems: [...newCartItems],
      },
    });

    // CRITICAL: Pass forceCreate=true to bypass React async state update timing issue
    const enrichedCart = await handlePaymentIntent(newCartItems, true);

    if (!enrichedCart || !enrichedCart.lineItems) {
      return;
    }

    setCartInLocalStorage(enrichedCart);
  }, [cart.lineItems, handlePaymentIntent, dispatch]); // Memoize with stable dependencies

  /**
   * Add item to cart
   *
   * WHY check for duplicates:
   * - Single-item cart enforced by business rules
   * - Prevents duplicate payment intents
   *
   * FIX: Wrapped in useCallback to prevent infinite render loops
   *
   * @param item - Item to add
   */
  const addToCart = useCallback(async (item: LineItem): Promise<void> => {
    // CRITICAL FIX: Single-item cart pattern - ALWAYS replace cart and create fresh payment intent
    // Previous check was preventing enrichCart when item existed, breaking payment intent creation
    await enrichCart(item);
  }, [enrichCart]); // Memoize with stable dependencies

  /**
   * DEV-1997: Store cart items immediately without creating a payment intent.
   * This lets usePurchase navigate to /checkout instantly while the checkout page
   * handles payment intent creation via createPaymentIntentFromCart.
   */
  const addToCartImmediate = useCallback((item: LineItem): void => {
    const newCartItems = [item];

    // Clear stale payment intent state from any previous cart
    setStripeClientSecret('');
    try { sessionStorage.removeItem(CHECKOUT_CLIENT_SECRET_KEY); } catch (e) { /* ignore */ }

    setCartInLocalStorage({ lineItems: newCartItems });
    dispatch({ type: 'cart/set', payload: { lineItems: newCartItems } });
  }, [dispatch]);

  /**
   * DEV-1997: Create payment intent for the given cart items.
   * Wraps handlePaymentIntent and persists the enriched cart to localStorage.
   */
  const createPaymentIntentFromCart = useCallback(async (items: LineItem[]): Promise<void> => {
    const enrichedCart = await handlePaymentIntent(items, true);
    if (enrichedCart?.lineItems) {
      setCartInLocalStorage(enrichedCart);
    }
  }, [handlePaymentIntent]);

  /** Empty the cart - Wrapped in useCallback to prevent infinite render loops */
  const emptyCart = useCallback((): void => {
    dispatch({ type: 'cart/empty' });
  }, [dispatch]); // Memoize with stable dependencies

  /**
   * Remove item from cart
   *
   * FIX: Wrapped in useCallback to prevent infinite render loops
   *
   * @param item - Item to remove
   */
  const removeFromCart = useCallback(async (item: LineItem): Promise<void> => {
    const newCartItems = cart?.lineItems?.filter(
      (i) => i.linked_id !== item.linked_id && i.type === item.type
    );

    setCartInLocalStorage({ lineItems: newCartItems });
    dispatch({ type: 'cart/set', payload: { lineItems: newCartItems } });
  }, [cart, dispatch]); // Memoize with stable dependencies

  /** Move cart to checkout stage - Wrapped in useCallback to prevent infinite render loops */
  const checkoutCart = useCallback(async (): Promise<void> => {
    dispatch({ type: 'cart/checkout' });
  }, [dispatch]); // Memoize with stable dependencies

  /**
   * Force order state with payment intent
   *
   * CRITICAL FIX: Wrapped in useCallback to prevent infinite render loops
   * - This function is used in CheckoutFormNew useEffect dependencies
   * - Without memoization, it causes hundreds of Stripe API calls
   *
   * @param paymentIntent - Enriched cart from server
   */
  const forceOrder = useCallback(async (paymentIntent: EnrichedCart): Promise<void> => {
    dispatch({ type: 'order/force', payload: paymentIntent });
  }, [dispatch]); // Memoize with stable dependencies

  /**
   * Add payment details
   *
   * FIX: Wrapped in useCallback to prevent infinite render loops
   *
   * @param data - Payment details
   */
  const addPaymentDetails = useCallback((data: PaymentDetails): void => {
    dispatch({ type: 'checkout/payment', payload: data });
  }, [dispatch]); // Memoize with stable dependencies

  /**
   * Confirm order completion
   *
   * WHY reset Stripe state:
   * - Prevents reusing payment intents
   * - Clears sensitive data
   * - Forces fresh intent creation for next purchase
   *
   * FIX: Wrapped in useCallback to prevent infinite render loops
   *
   * @param data - Order confirmation data
   */
  const confirmOrder = useCallback((data?: unknown): void => {
    setStripePromise(null);
    setStripeClientSecret('');
    try { sessionStorage.removeItem(CHECKOUT_CLIENT_SECRET_KEY); } catch (e) { /* ignore */ }
    dispatch({ type: 'order/confirmed', payload: data });
    resetCartInLocalStorage();
  }, [dispatch]); // Memoize with stable dependencies

  /**
   * Update payment details
   *
   * FIX: Wrapped in useCallback to prevent infinite render loops
   *
   * @param data - Partial payment update
   */
  const updatePayment = useCallback((data: Partial<PaymentDetails>): void => {
    dispatch({ type: 'payment/update', payload: data });
  }, [dispatch]); // Memoize with stable dependencies

  /** Clear checkout state - Wrapped in useCallback to prevent infinite render loops */
  const clear = useCallback((): void => {
    dispatch({ type: 'data/reset' });
    setStripeClientSecret('');
    resetCartInLocalStorage();
  }, [dispatch]); // Memoize with stable dependencies

  /**
   * Set checkout stage
   *
   * FIX: Wrapped in useCallback to prevent infinite render loops
   *
   * @param stage - New stage
   */
  const setStage = useCallback((stage: CheckoutStage): void => {
    dispatch({ type: 'data/stage', payload: stage });
  }, [dispatch]); // Memoize with stable dependencies

  /**
   * Save cart to localStorage
   *
   * @param data - Cart data to save
   */
  function setCartInLocalStorage(data: unknown): void {
    if (data !== undefined) {
      localStorage.setItem(LS_CART_KEY, JSON.stringify(data));
    }
  }

  /** Clear cart from localStorage */
  function resetCartInLocalStorage(): void {
    localStorage.removeItem(LS_CART_KEY);
    localStorage.removeItem('redirect');
  }

  /** Load cart from localStorage */
  function getCartInLocalStorage(): Cart | null {
    const data = localStorage.getItem(LS_CART_KEY);
    return data ? (JSON.parse(data) as Cart) : null;
  }

  /** Get last completed order - Wrapped in useCallback to prevent infinite render loops */
  const getLastOrder = useCallback((): EnrichedCart => {
    return lastOrder;
  }, [lastOrder]); // Memoize with stable dependencies

  /**
   * DEV-2132: Clear context error so a user retry can dismiss the persistent
   * error message set by `handlePaymentIntent` on failure. Stable identity via
   * useCallback so it can be used in effect deps without re-creation.
   */
  const clearError = useCallback((): void => {
    setError('');
  }, []);

  // Load cart from localStorage on mount
  // DEV-1936: Also recover clientSecret from sessionStorage for page refresh
  useEffect(() => {
    let mounted = true;

    async function loadCart() {
      const data = getCartInLocalStorage();

      if (!data) return;

      dispatch({ type: 'cart/set', payload: data });

      // DEV-1936: Recover PaymentIntent after page refresh
      // WHY: The PaymentIntent is still valid on Stripe's side. We just lost the
      // local reference. Re-initialize Stripe with the saved clientSecret.
      try {
        const savedSecret = sessionStorage.getItem(CHECKOUT_CLIENT_SECRET_KEY);
        if (savedSecret && !stripeClientSecret) {
          const publishableKeyResponse = await getPublishableKey();
          if (!mounted) return;
          const publishableKey =
            publishableKeyResponse?.publishableKey ||
            (publishableKeyResponse as any)?.publishablekey;
          if (publishableKey) {
            setStripeClientSecret(savedSecret);
            setStripePromise(loadStripe(publishableKey));
          }
        }
      } catch (e) {
        // Non-fatal: user will see an error and can retry
        console.warn('DEV-1936: Failed to recover checkout state after refresh:', e);
      }
    }

    loadCart();
    return () => { mounted = false; };
  }, []);

  const value: CheckoutContextValue = {
    stage,
    cart,
    payment,
    order,
    addToCart,
    addToCartImmediate,
    createPaymentIntentFromCart,
    emptyCart,
    removeFromCart,
    checkoutCart,
    forceOrder,
    addPaymentDetails,
    confirmOrder,
    updatePayment,
    clear,
    setStage,
    isLoading,
    stripePromise,
    setStripePromise,
    stripeClientSecret,
    setStripeClientSecret,
    error,
    lastOrder,
    getLastOrder,
    clearError,
  };

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
}

/**
 * Custom hook to access checkout context
 *
 * @returns Checkout context value
 * @throws {Error} If used outside CheckoutContextProvider
 */
export function useCheckout(): CheckoutContextValue {
  const context = useContext(CheckoutContext);

  if (context === undefined) {
    throw new Error('useCheckout must be used within CheckoutContextProvider');
  }

  return context;
}
